import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl, setApiUrl } from './apiConfig';
import Dashboard from './components/Dashboard';
import WalletManager from './components/WalletManager';
import StatsPage from './components/StatsPage';
import PortfolioPage from './components/PortfolioPage';
import SettingsPage from './components/SettingsPage';
import MockTradesPage from './components/MockTradesPage';
import MockStatisticsPage from './components/MockStatisticsPage';
import MockPortfolioPage from './components/MockPortfolioPage';
import MockTransactionsPage from './components/MockTransactionsPage';
import MockDepositPage from './components/MockDepositPage';
import TransactionsPage from './components/TransactionsPage';
import Navigation from './components/Navigation';

function App() {
  const [trades, setTrades] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [mockSubPage, setMockSubPage] = useState('trades');

  // Resolve API URL (runtime from /config.json or build-time REACT_APP_API_URL)
  const resolveApiUrl = () => {
    const finalUrl = getApiUrl();
    setDebugInfo({
      finalApiUrl: finalUrl,
      nodeEnv: process.env.NODE_ENV,
      allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      timestamp: new Date().toISOString()
    });
    return finalUrl;
  };

  // Initial data fetch with loading state
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setConnectionStatus('connecting');
        
        const apiUrl = resolveApiUrl();
      if (!apiUrl) {
        setError('API URL is not configured. Set REACT_APP_API_URL or API_URL in the frontend app env (CapRover: App Configs).');
        setConnectionStatus('error');
        setLoading(false);
        return;
      }
      console.log(`Initial fetch from: ${apiUrl}`);

      // Create axios instance with timeout
      const axiosInstance = axios.create({
          timeout: 15000, // 15 second timeout
          headers: {
          'Content-Type': 'application/json'
        }
      });

      // Fetch trades and wallets separately to allow partial loading
      const promises = [
        axiosInstance.get(`${apiUrl}/trades`).catch(err => ({ error: err, data: [] })),
        axiosInstance.get(`${apiUrl}/wallets`).catch(err => ({ error: err, data: [] }))
      ];

      const [tradesResponse, walletsResponse] = await Promise.all(promises);

      // Handle trades response
      if (tradesResponse.error) {
        console.error('Failed to fetch trades:', tradesResponse.error.message);
      } else {
        setTrades(tradesResponse.data || []);
      }

      // Handle wallets response
      if (walletsResponse.error) {
        console.error('Failed to fetch wallets:', walletsResponse.error.message);
      } else {
        setWallets(walletsResponse.data || []);
      }

      // If both failed, show error
      if (tradesResponse.error && walletsResponse.error) {
        throw tradesResponse.error;
      }

        setConnectionStatus('connected');
      console.log('Initial data fetched successfully');
        
      } catch (err) {
      console.error('Error fetching initial data:', err);
      setConnectionStatus('error');
      
      let errorMessage = 'Connection failed: ';
        
        if (err.code === 'ECONNABORTED') {
        errorMessage += 'Request timeout. The server took too long to respond.';
        } else if (err.response?.status === 404) {
        errorMessage += 'API endpoint not found. Please verify the backend URL.';
        } else if (err.response?.status >= 500) {
        errorMessage += 'Server error. Please try again later.';
      } else if (err.message.includes('Network Error')) {
        errorMessage += 'Network error. Please check your internet connection.';
      } else if (!getApiUrl()) {
        errorMessage += 'API URL is not configured. Set REACT_APP_API_URL or API_URL in the frontend app env (CapRover).';
        } else {
        errorMessage += err.message || 'Unknown error occurred.';
        }
        
        setError(errorMessage);
    } finally {
        setLoading(false);
    }
  };

  // Periodic data refresh without loading state - just updates data silently
  const refreshData = async () => {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) return;
      console.log(`Background refresh from: ${apiUrl}`);

      // Create axios instance with timeout
      const axiosInstance = axios.create({
        timeout: 10000, // Shorter timeout for background refresh
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Fetch trades and wallets separately
      const promises = [
        axiosInstance.get(`${apiUrl}/trades`).catch(err => ({ error: err, data: null })),
        axiosInstance.get(`${apiUrl}/wallets`).catch(err => ({ error: err, data: null }))
      ];

      const [tradesResponse, walletsResponse] = await Promise.all(promises);

      // Update trades only if successful
      if (!tradesResponse.error && tradesResponse.data) {
        setTrades(tradesResponse.data);
        console.log('Trades updated silently');
      }

      // Update wallets only if successful
      if (!walletsResponse.error && walletsResponse.data) {
        setWallets(walletsResponse.data);
        console.log('Wallets updated silently');
      }

      // Update connection status based on at least one successful response
      if (!tradesResponse.error || !walletsResponse.error) {
        setConnectionStatus('connected');
      }
      
    } catch (err) {
      console.error('Background refresh failed (non-critical):', err);
      // Don't update error state for background refresh failures
    }
  };

  // Handle page navigation
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Reset mock sub-page when switching away from mock-trades
    if (page !== 'mock-trades') {
      setMockSubPage('trades');
    }
  };

  // Handle mock sub-page navigation
  const handleMockSubPageChange = (subPage) => {
    setMockSubPage(subPage);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load runtime config first (CapRover: REACT_APP_API_URL or API_URL in frontend env)
      try {
        const res = await fetch('/config.json');
        if (res.ok) {
          const data = await res.json();
          if (data.apiUrl) setApiUrl(data.apiUrl);
        }
      } catch (e) {
        console.warn('Could not load /config.json, using build-time API URL if set:', e.message);
      }
      if (!cancelled) fetchData();
    })();
    const interval = setInterval(() => refreshData(), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Show loading state
  if (loading && !error) {
    return (
      <div className="min-h-screen bg-black">
        <div className="container mx-auto px-4 py-8">
          <header className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-2" style={{ color: '#22c55e' }}>
              The Melon Central
            </h1>
            <p className="text-xl text-gray-300">Advanced automation platform</p>
          </header>
          
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
              <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 opacity-20" style={{ borderColor: '#22c55e', borderWidth: '1px', borderStyle: 'solid' }}></div>
            </div>
            <p className="text-gray-300 text-lg mb-2 mt-6">Loading dashboard...</p>
            <div className="text-sm text-gray-500">
              <p>Connecting to backend services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <div className="container mx-auto px-4 py-8">
          <header className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-2" style={{ color: '#22c55e' }}>
              The Melon Central
            </h1>
            <p className="text-xl text-gray-300">Advanced automation platform</p>
          </header>
          
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="bg-black/80 border border-red-500/30 rounded-2xl p-8 max-w-2xl backdrop-blur-xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-4">Connection Error</h2>
              </div>
              <p className="text-red-300 mb-6 text-center">{error}</p>
              
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-red-400 mb-2">Troubleshooting steps:</h3>
                <ul className="text-red-300 text-sm space-y-1">
                  <li>• Verify the backend service is running</li>
                  <li>• Set REACT_APP_API_URL or API_URL in frontend app env (CapRover: App Configs)</li>
                  <li>• Ensure CORS is properly configured</li>
                  <li>• Confirm network connectivity</li>
                  </ul>
                </div>
                
                <button 
                onClick={fetchData}
                className="btn btn-primary w-full mb-6"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Connection
                </button>
              
              <div className="text-xs text-gray-400 bg-black/60 border border-primary-600/20 p-4 rounded-xl backdrop-blur-sm">
                <h4 className="font-semibold mb-3 text-primary-400">Debug Information</h4>
                <div className="space-y-1">
                  <p><span className="text-primary-500">API URL:</span> {debugInfo?.finalApiUrl || 'undefined'}</p>
                  <p><span className="text-primary-500">Environment:</span> {debugInfo?.nodeEnv || 'unknown'}</p>
                  <p><span className="text-primary-500">Connection:</span> {connectionStatus}</p>
                  <p><span className="text-primary-500">Timestamp:</span> {debugInfo?.timestamp}</p>
                  <p><span className="text-primary-500">Variables:</span> {debugInfo?.allEnvVars?.length ? debugInfo.allEnvVars.join(', ') : 'none'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show main application
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f100f 0%, #101010 100%)' }}>
      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="relative">
            <h1 className="text-6xl font-bold mb-2 animate-fade-in" style={{ color: '#22c55e' }}>
              The Melon Central
            </h1>
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-600/20 to-transparent blur-xl opacity-30 animate-glow"></div>
          </div>
          <p className="text-xl text-gray-300 mb-6">Advanced automation platform</p>
          <div className="mt-4">
            <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm border transition-all duration-300 ${
              connectionStatus === 'connected' ? 'bg-primary-900/30 text-primary-400 border-primary-600/30 shadow-lg shadow-primary-500/10' : 
              connectionStatus === 'connecting' ? 'bg-yellow-900/30 text-yellow-400 border-yellow-600/30' : 
              'bg-red-900/30 text-red-400 border-red-600/30'
            }`}>
              <div className={`w-3 h-3 rounded-full mr-3 ${
              connectionStatus === 'connected' ? 'bg-primary-400 shadow-lg shadow-primary-400/50' :
                connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' : 
              'bg-red-400'
            }`}></div>
              {connectionStatus === 'connected' ? 'Connected' : 
               connectionStatus === 'connecting' ? 'Connecting...' : 
               'Disconnected'}
            </span>
          </div>
        </header>

        {/* Navigation */}
        <Navigation currentPage={currentPage} onPageChange={handlePageChange} />

        {/* Page Content */}
        {currentPage === 'dashboard' && (
          <Dashboard trades={trades} />
        )}
        
        {currentPage === 'stats' && (
          <StatsPage trades={trades} wallets={wallets} />
        )}
        
        {currentPage === 'wallet' && (
          <WalletManager wallets={wallets} onWalletUpdate={fetchData} />
        )}
        
        {currentPage === 'portfolio' && (
          <PortfolioPage trades={trades} wallets={wallets} />
        )}

        {currentPage === 'settings' && (
          <SettingsPage />
        )}

        {currentPage === 'transactions' && (
          <TransactionsPage trades={trades} />
        )}

        {currentPage === 'mock-trades' && (
          <div className="space-y-6">
            {/* Mock Trading Sub-Navigation */}
            <div className="card">
              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                {[
                  { id: 'trades', label: 'Mock Trades', description: 'Simulated trading dashboard' },
                  { id: 'statistics', label: 'Mock Statistics', description: 'Performance analytics' },
                  { id: 'portfolio', label: 'Mock Portfolio', description: 'Portfolio overview' },
                  { id: 'transactions', label: 'Mock Transactions', description: 'Transaction history' },
                  { id: 'deposit', label: 'Mock Deposit', description: 'Deposit SOL to mock portfolio' }
                ].map((item) => {
                  const isActive = mockSubPage === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleMockSubPageChange(item.id)}
                      className={`group relative flex items-center space-x-3 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                        isActive
                          ? 'bg-gradient-to-r from-primary-600/80 to-black/80 shadow-lg shadow-primary-500/25 border border-primary-500/30'
                          : 'bg-black/40 text-gray-300 hover:bg-black/60 hover:text-[#22c55e] border border-gray-700/30 hover:border-primary-600/30'
                      }`}
                      style={isActive ? { color: '#22c55e' } : {}}
                      title={item.description}
                    >
                      <span className="relative">
                        {item.label}
                        {isActive && (
                          <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-transparent rounded-full"></div>
                        )}
                      </span>
                      
                      {/* Hover effect */}
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-primary-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isActive ? 'opacity-20' : ''}`}></div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mock Trading Sub-Page Content */}
            {mockSubPage === 'trades' && (
              <MockTradesPage />
            )}
            
            {mockSubPage === 'statistics' && (
              <MockStatisticsPage />
            )}
            
            {mockSubPage === 'portfolio' && (
              <MockPortfolioPage />
            )}
            
            {mockSubPage === 'transactions' && (
              <MockTransactionsPage />
            )}
            
            {mockSubPage === 'deposit' && (
              <MockDepositPage />
            )}
          </div>
        )}
        </div>
      </div>
    );
}

export default App;

